import { ReactNode, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Check, ChevronDown, Search, UsersRound, type LucideIcon } from 'lucide-react';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import StatsGrid, { type Stat } from '@/client/components/common/StatsGrid';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { salesKeys } from '@/client/api/sales/keys';
import { useSalesRepsQuery, useSalesSelfCheckQuery } from '@/client/api/sales/sales';
import { SALES_REP_ROLE_LABEL, SALES_VERTICALS, VERTICAL_LABEL, type SalesRep } from '@/client/types/sales';
import { NO_ONE, closeActAsPrompt, setCurrentRep, useSalesActing, useSalesState } from '../sales-store';
import { initials } from './sales-ui';

interface SalesPageShellProps {
  title: string;
  description: string;
  crumbs: { label: string; href?: string }[];
  icon: LucideIcon;
  actions?: ReactNode;
  children: ReactNode;
  stats?: Stat[];
}

function deskLabel(desk: SalesRep['desk']): string {
  return desk === 'all' ? 'All desks' : VERTICAL_LABEL[desk];
}

function Avatar({ rep, everyone }: { rep?: SalesRep | null; everyone?: boolean }) {
  if (everyone || !rep) {
    return (
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-800 text-white">
        <UsersRound className="h-3.5 w-3.5" />
      </span>
    );
  }
  return (
    <span
      className={cn(
        'flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold text-white',
        rep.avatarColor,
      )}
    >
      {initials(rep.name)}
    </span>
  );
}

/** The roster grouped by desk, filtered by a search string. */
function useRepGroups(query: string) {
  const repsQ = useSalesRepsQuery();
  const liveReps = repsQ.data ?? [];
  return useMemo(() => {
    return (['all', ...SALES_VERTICALS] as const)
      .map((desk) => ({
        desk,
        label: desk === 'all' ? 'Head of sales' : VERTICAL_LABEL[desk],
        reps: liveReps.filter((r) => {
          if (r.desk !== desk) return false;
          if (!query) return true;
          const blob = `${r.name} ${r.title} ${deskLabel(r.desk)} ${SALES_REP_ROLE_LABEL[r.role]}`.toLowerCase();
          return blob.includes(query);
        }),
      }))
      .filter((g) => g.reps.length > 0);
  }, [query, liveReps]);
}

function ActingAsPicker() {
  const { rep, canActAsAnyone } = useSalesActing();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const everyone = !rep;
  const query = q.trim().toLowerCase();
  const groups = useRepGroups(query);

  const everyoneVisible = !query || 'everyone whole floor all staff'.includes(query);

  const pick = (id: string) => {
    setCurrentRep(id);
    void qc.invalidateQueries({ queryKey: salesKeys.all });
    setOpen(false);
    setQ('');
  };

  const trigger = (
    <span className="flex min-w-0 items-center gap-2">
      <Avatar rep={rep} everyone={everyone} />
      <span className="min-w-0 text-left">
        <span className="block truncate text-sm font-medium leading-tight text-gray-900">
          {everyone ? 'Everyone' : rep?.name}
        </span>
        <span className="block truncate text-[11px] leading-tight text-gray-500">
          {everyone ? 'Whole floor' : rep ? `${SALES_REP_ROLE_LABEL[rep.role]} · ${deskLabel(rep.desk)}` : ''}
        </span>
      </span>
    </span>
  );

  if (!canActAsAnyone) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white py-1 pl-1 pr-3" aria-label="Signed in as">
        {trigger}
      </div>
    );
  }

  return (
    <DropdownMenu
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setQ('');
      }}
    >
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="Viewing as"
          className="flex max-w-[16rem] items-center gap-2 rounded-lg border border-gray-200 bg-white py-1 pl-1 pr-2 text-left transition-colors hover:border-gray-300 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-900 active:scale-[0.97]"
        >
          {trigger}
          <ChevronDown className="h-3.5 w-3.5 shrink-0 text-gray-400" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 p-0" onCloseAutoFocus={(e) => e.preventDefault()}>
        <div className="border-b border-gray-100 p-2">
          <p className="px-1 pb-1.5 text-[11px] font-medium uppercase tracking-wide text-gray-500">Viewing as</p>
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search people…"
              className="h-9 w-full rounded-md border border-gray-200 bg-white pl-8 pr-2 text-sm text-gray-900 outline-none focus:border-slate-900"
              onKeyDown={(e) => e.stopPropagation()}
            />
          </div>
        </div>
        <div className="max-h-72 overflow-y-auto p-1">
          {everyoneVisible && (
            <DropdownMenuItem
              className="gap-2 rounded-md px-2 py-2"
              onSelect={() => pick(NO_ONE)}
            >
              <Avatar everyone />
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-medium text-gray-900">Everyone</span>
                <span className="block text-[11px] text-gray-500">Whole floor</span>
              </span>
              {everyone ? <Check className="h-4 w-4 text-slate-900" /> : null}
            </DropdownMenuItem>
          )}
          {groups.map((g) => (
            <div key={g.desk}>
              <p className="px-2 pb-1 pt-2 text-[11px] font-medium uppercase tracking-wide text-gray-400">{g.label}</p>
              {g.reps.map((r) => {
                const selected = rep?.id === r.id;
                return (
                  <DropdownMenuItem
                    key={r.id}
                    className="gap-2 rounded-md px-2 py-2"
                    onSelect={() => pick(r.id)}
                  >
                    <Avatar rep={r} />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-gray-900">{r.name}</span>
                      <span className="block truncate text-[11px] text-gray-500">{r.title}</span>
                    </span>
                    {selected ? <Check className="h-4 w-4 shrink-0 text-slate-900" /> : null}
                  </DropdownMenuItem>
                );
              })}
            </div>
          ))}
          {!everyoneVisible && groups.length === 0 && (
            <p className="px-2 py-6 text-center text-sm text-gray-500">No matches</p>
          )}
        </div>
        {rep && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem className="px-3 py-2 text-sm text-gray-600" onSelect={() => pick(NO_ONE)}>
              Back to everyone
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** Opened by `promptActAs()` when an action needs a specific rep and the
 *  viewer is on Everyone (or is not on the floor at all). */
function ActAsPrompt() {
  const { actAsPrompt } = useSalesState();
  const { canActAsAnyone } = useSalesActing();
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const groups = useRepGroups(q.trim().toLowerCase());

  const close = () => {
    closeActAsPrompt();
    setQ('');
  };
  const pick = (r: SalesRep) => {
    setCurrentRep(r.id);
    void qc.invalidateQueries({ queryKey: salesKeys.all });
    setQ('');
    toast.success(`Acting as ${r.name}. Try again.`);
  };

  return (
    <Dialog open={actAsPrompt !== null} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-md gap-4">
        <DialogHeader>
          <DialogTitle>{canActAsAnyone ? 'Who are you acting as?' : 'You are not on the sales floor'}</DialogTitle>
          <DialogDescription>
            {canActAsAnyone
              ? `To ${actAsPrompt}, pick the rep it is done for. Everyone is a view of the whole floor and cannot own work.`
              : `To ${actAsPrompt}, you need to be a rep. Ask a sales manager to add you under Sales → Team.`}
          </DialogDescription>
        </DialogHeader>
        {canActAsAnyone && (
          <>
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
              <input
                autoFocus
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search people…"
                className="h-9 w-full rounded-md border border-gray-200 bg-white pl-8 pr-2 text-sm text-gray-900 outline-none focus:border-slate-900"
              />
            </div>
            <div className="-mx-1 max-h-80 overflow-y-auto">
              {groups.map((g) => (
                <div key={g.desk}>
                  <p className="px-2 pb-1 pt-2 text-[11px] font-medium uppercase tracking-wide text-gray-400">{g.label}</p>
                  {g.reps.map((r) => (
                    <button
                      key={r.id}
                      type="button"
                      className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-left hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-900"
                      onClick={() => pick(r)}
                    >
                      <Avatar rep={r} />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium text-gray-900">{r.name}</span>
                        <span className="block truncate text-[11px] text-gray-500">
                          {SALES_REP_ROLE_LABEL[r.role]} · {deskLabel(r.desk)}
                        </span>
                      </span>
                    </button>
                  ))}
                </div>
              ))}
              {groups.length === 0 && (
                <p className="px-2 py-6 text-center text-sm text-gray-500">
                  {q ? 'No matches' : 'No reps yet. Add one under Sales → Team.'}
                </p>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function FloorCheckBanner() {
  const check = useSalesSelfCheckQuery();
  const rows = (check.data?.rows ?? []).filter((r) => r.severity === 'error');
  if (rows.length === 0) return null;
  return (
    <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-950">
      <p className="font-medium">Floor is not ready — {rows.length} {rows.length === 1 ? 'error' : 'errors'}</p>
      <ul className="mt-1 list-disc space-y-0.5 pl-5 text-amber-900/90">
        {rows.map((r) => (
          <li key={`${r.code}-${r.message}`}>
            {r.message} {r.fix}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Shared chrome for Sales. Crumbs are Admin / Sales / … */
export function SalesPageShell({
  title,
  description,
  crumbs,
  icon: Icon,
  actions,
  children,
  stats = [],
}: SalesPageShellProps) {
  useBreadcrumbs([{ label: 'Admin', href: '/admin' }, { label: 'Sales', href: '/admin/sales' }, ...crumbs]);
  const { clockOffsetMs } = useSalesState();
  const daysAhead = Math.round(clockOffsetMs / 86400_000);

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="mb-1 flex items-center gap-2.5">
            <Icon className="h-6 w-6 text-slate-700" />
            <h1 className="text-2xl font-semibold tracking-tight text-gray-900">{title}</h1>
            {daysAhead > 0 && (
              <span className="rounded-full border border-violet-200 bg-violet-50 px-2 py-0.5 text-[11px] font-medium text-violet-800">
                Clock +{daysAhead}d
              </span>
            )}
          </div>
          <p className="ml-9 max-w-xl text-sm text-gray-500">{description}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <ActingAsPicker />
          {actions}
        </div>
      </div>

      {stats.length > 0 && <StatsGrid stats={stats} />}

      <FloorCheckBanner />

      {children}

      <ActAsPrompt />
    </div>
  );
}
